<script setup lang="ts">
import { computed, ref } from "vue";
import Icon from "@/design/primitives/Icon.vue";

/**
 * Ben, as a thing that is alive.
 *
 * ONE component, ONE source of truth (`state`), every state expressed in CSS. That last
 * part is the whole design: an orb driven by JavaScript timers stutters whenever the main
 * thread is busy — which, during a streaming turn, is exactly when it is being watched.
 *
 * ── REBUILT TO V2's MODEL, AND WHY THE FIRST VERSION READ AS A BLUE BALL ──
 *
 * The first version was one filled circle in the app's accent colour with a concentric
 * pulse. V2's `BenAvatar.vue` is 526 lines and 23 animations, and reading it made the
 * difference obvious: it is not a ball, it is FOUR layers.
 *
 *   shell  a violet gradient lit from the upper left, with two very subtle inset
 *          shadows for volume. V2's own comment records getting this wrong once — an
 *          inset shadow at 0.55 alpha turned the bottom half black and "cut" the sphere.
 *          These are 0.18 and 0.08.
 *   aura   the state's colour, screen-blended, breathing on its own clock.
 *   core   an inner circle at 56%, BLURRED and transparent at its edges, drifting on a
 *          four-point path. This is what makes it read as light inside glass rather than
 *          paint on a disc, and its absence is the single reason mine looked flat.
 *   spark  a golden point at 22%, offset off-centre. V2 calls it "a alma", and its
 *          comment says it was made bigger and brighter so it reads as the orb's nucleus
 *          rather than a stray reflection. It is the only warm colour in a cool object,
 *          which is why the eye goes to it.
 *
 * FOUR INDEPENDENT DURATIONS — 5.2s, 6.4s, 7.8s, 3.1s — divided by one speed multiplier.
 * They deliberately do not divide evenly into each other: loops that re-align every cycle
 * produce a pattern the eye finds within seconds, and a found pattern is a mechanism
 * rather than a creature.
 *
 * ── THE STATES ────────────────────────────────────────────────────────────
 *
 * V2 has ten. These six carry information V3 actually has:
 *
 *   idle      — here, not doing anything
 *   thinking  — a turn is running, no output yet
 *   typing    — text is arriving
 *   acting    — a tool is running (the ring: something is happening OUT there)
 *   success   — the turn finished and wrote something
 *   trouble   — it failed, and the orb should not pretend otherwise
 *
 * `listening`, `celebration` and `offline` are dropped with reasons: V3 has no voice
 * input, confetti on a finished turn is a lie about how much it mattered, and a
 * disconnected panel shows a banner rather than a sad orb.
 *
 * The point is not decoration. A model turn has no progress bar and no honest ETA, so the
 * only thing separating "working" from "hung" is whether the thing is visibly breathing.
 *
 * ── A FOTO LÁ DENTRO ──────────────────────────────────────────────────────
 *
 * A V2 punha dentro do orbe o que a pesquisa ia encontrando — o logotipo, a foto
 * do perfil, a imagem do site — e é essa a peça que fazia o primeiro dia dela ser
 * bonito em vez de ser uma espera. `photo` é essa camada, e é OPCIONAL em toda a
 * parte: sem ela o orbe é exactamente o orbe de sempre, sem moldura vazia e sem
 * espaço reservado. Um orbe que reservasse lugar para uma imagem que nunca vem é
 * um buraco com um assistente à volta.
 *
 * Ela entra POR CIMA do núcleo e POR BAIXO da faísca e do brilho, de propósito: a
 * faísca é a alma do objeto e um `cover` por cima dela apagaria o que faz aquilo
 * parecer vivo. O resultado é a foto DENTRO do vidro, e não colada à frente dele.
 */
export type OrbState = "idle" | "thinking" | "typing" | "acting" | "success" | "trouble";

const props = withDefaults(
	defineProps<{
		state?: OrbState;
		size?: number;
		/**
		 * A imagem que vive dentro do orbe agora — o logotipo capturado, a foto de
		 * um perfil, o `og:image` de um site. `null` é o caso normal.
		 */
		photo?: string | null;
		/**
		 * O ORBE DO NEGÓCIO (04/10). Presente — mesmo vazio — é o orbe do primeiro
		 * dia, que é do cliente e não do Ben: sem a faísca dourada (a alma é DO
		 * Ben). Com cores (hex, a primeira é a da marca), o vidro tinge-se delas
		 * devagar. Ausente é o Ben de sempre, intacto.
		 */
		palette?: readonly string[] | null;
		/** Muda quando algo foi encontrado: o orbe pulsa uma vez. */
		beat?: number;
		/**
		 * O COPILOTO (18/10): o mesmo Ben, com núcleo e faísca, em laranja — quem o
		 * distingue do Ben interno é a cor, e não outro desenho. Não é o `palette`:
		 * esse é o orbe do negócio, que perde a alma de propósito.
		 */
		tone?: "ben" | "copilot";
	}>(),
	{
		state: "idle",
		size: 56,
		photo: null,
		palette: null,
		beat: 0,
		tone: "ben",
	},
);

/** "#abc" / "#aabbcc" / "#aabbccdd" como "r, g, b" — a auréola compõe a sua própria opacidade. */
function rgbOf(hex: string): string | null {
	let h = hex.trim().replace(/^#/, "");
	if (h.length === 3) h = [...h].map((c) => c + c).join("");
	if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(h)) return null;
	return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16)).join(", ");
}

/** As cores da marca como variáveis: dados, só no orbe — o CSS decide o que fazer delas. */
const brand = computed((): Record<string, string> => {
	const colors = (props.palette ?? []).filter((c) => rgbOf(c));
	const a = colors[0];
	if (!a) return {};
	const b = colors[1] ?? a;
	return { "--brand-a": a, "--brand-b": b, "--brand-rgb": rgbOf(b) ?? "" };
});

/**
 * A URL que não carregou, esquecida.
 *
 * A imagem vem da web e uma URL da web pode ter morrido entre a missão e este
 * instante. O `@error` devolve o orbe ao que ele é — que é a queda certa, porque
 * o orbe nunca dependeu de imagem nenhuma para dizer o que está a acontecer.
 */
const broken = ref<string | null>(null);
const shown = computed(() => (props.photo && props.photo !== broken.value ? props.photo : null));
/** A URL que falhou, dita a quem escolhe as fotos (30/09): a galeria tira-a da rotação. */
const emit = defineEmits<{ broken: [url: string] }>();
function fail(): void {
	if (!shown.value) return;
	broken.value = shown.value;
	emit("broken", shown.value);
}
</script>

<template>
	<span
		class="ben-orb"
		:class="[
			`ben-orb--${props.state}`,
			{
				'ben-orb--business': !!props.palette,
				'ben-orb--brand': !!brand['--brand-a'],
				'ben-orb--copilot': props.tone === 'copilot',
			},
		]"
		:style="{ '--orb-size': `${props.size}px`, ...brand }"
		aria-hidden="true"
	>
		<!-- Every layer lives INSIDE the shell, which clips. Nothing escapes the circle,
		     so an orb in a crowded header cannot bleed over its neighbours. -->
		<span class="ben-orb__shell">
			<span class="ben-orb__aura" />
			<!--
				O orbe do NEGÓCIO não tem núcleo nem centelha (dono, 04/10: "esse amarelo no meio
				deve ser removido na orbe do negócio"). Pintado com a segunda cor da marca, o núcleo
				devolvia o mesmo clarão — dourado no Instituto Venere — por cima do logo.
			-->
			<span v-if="!props.palette" class="ben-orb__core" />
			<!-- A `key` é a URL: uma foto NOVA entra por cima da anterior em vez de
			     trocar o `src` no lugar, que é o que faz o corte seco. -->
			<Transition name="orb-photo">
				<img
					v-if="shown"
					:key="shown"
					:src="shown"
					class="ben-orb__photo"
					alt=""
					loading="lazy"
					@error="fail"
				/>
			</Transition>
			<span v-if="!props.palette" class="ben-orb__spark" />
			<span class="ben-orb__sheen" />
		</span>

		<!-- Per-state layers. Outside the shell only where they must be: the ring is a
		     halo AROUND the orb, and clipping it would hide the half that reads. -->
		<span v-if="state === 'acting'" class="ben-orb__ring" />
		<span v-if="props.beat" :key="props.beat" class="ben-orb__beat" />
		<span v-if="state === 'typing'" class="ben-orb__dots"><i /><i /><i /></span>
		<span v-else-if="state === 'success'" class="ben-orb__mark"><Icon name="check" class="size-[1em]" /></span>
		<span v-else-if="state === 'trouble'" class="ben-orb__mark ben-orb__mark--bad">!</span>
	</span>
</template>

<style scoped>
.ben-orb {
	--orb-size: 56px;

	/*
	 * A paleta do orbe — a mesma FAMÍLIA do acento, e não o mesmo token.
	 *
	 * Ben é um personagem; o acento é uma cor de interface. Um botão e um
	 * assistente não devem ser o mesmo azul, e amarrá-los faria uma mudança de
	 * tema virar outro Ben. O que mudou aqui foi a família: a rampa da V2 era
	 * violeta e foi ROTACIONADA para o azul do produto (217°), com saturação e
	 * luminosidade de cada degrau preservadas — a profundidade do objeto é a
	 * mesma, só a cor é outra.
	 */
	--orb-top: #206de8;
	--orb-shell: #0a3d8f;
	--orb-deep: #000f28;
	--orb-rim: #000814;

	--core-mid: #76abff;
	--core-bright: #d4e4ff;
	--core-edge: 196, 118, 255;

	/* The one warm colour in a cool object. */
	--spark: #ffc24a;
	--spark-bright: #ffe79a;

	/* A auréola, trocada por estado. Azul em repouso. */
	--aura-rgb: 32, 109, 232;
	--glow: 1;

	/* One multiplier, four derived clocks. See the header on why they are co-prime-ish. */
	--speed: 1;
	--dur-breath: calc(5.2s / var(--speed));
	--dur-aura: calc(6.4s / var(--speed));
	--dur-core: calc(7.8s / var(--speed));
	--dur-spark: calc(3.1s / var(--speed));

	position: relative;
	display: inline-block;
	width: var(--orb-size);
	height: var(--orb-size);
	flex: none;
}

/* ── The shell ───────────────────────────────────────────────────────────── */

.ben-orb__shell {
	position: absolute;
	inset: 0;
	border-radius: 50%;
	overflow: hidden;
	background: radial-gradient(
		circle at 32% 28%,
		var(--orb-top) 0%,
		var(--orb-shell) 38%,
		var(--orb-deep) 74%,
		var(--orb-rim) 100%
	);
	/*
	 * Volume, barely. V2's comment records the mistake worth inheriting: an inset shadow
	 * at 0.55 alpha turned the bottom half black and cut the sphere in two.
	 */
	box-shadow:
		inset 0 -4px 10px rgba(13, 0, 20, 0.18),
		inset 0 4px 10px rgba(255, 255, 255, 0.08);
	animation: orb-breath var(--dur-breath) ease-in-out infinite;
	/* 600ms, so a state change is a mood shift rather than a flicker. */
	transition: background 600ms ease;
	will-change: transform;
}

.ben-orb__aura {
	position: absolute;
	inset: 0;
	border-radius: 50%;
	background: radial-gradient(
		circle at 50% 50%,
		rgba(var(--aura-rgb), calc(0.55 * var(--glow))) 0%,
		rgba(var(--aura-rgb), calc(0.18 * var(--glow))) 38%,
		rgba(var(--aura-rgb), 0) 70%
	);
	mix-blend-mode: screen;
	animation: orb-aura var(--dur-aura) ease-in-out infinite;
	transition: background 600ms ease;
}

/*
 * The core: light INSIDE the glass.
 *
 * 56% and transparent by 85%, so it never reaches the rim — a core that touches the edge
 * is a second disc, and the whole illusion is that this is suspended in something.
 * `screen` so it brightens rather than covers; `blur(1px)` so it has no edge of its own.
 */
.ben-orb__core {
	position: absolute;
	left: 50%;
	top: 50%;
	width: 56%;
	height: 56%;
	transform: translate(-50%, -50%);
	border-radius: 50%;
	background: radial-gradient(
		circle at 50% 50%,
		var(--core-bright) 0%,
		var(--core-mid) 30%,
		rgba(var(--core-edge), 0.5) 58%,
		rgba(var(--core-edge), 0) 85%
	);
	filter: blur(1px);
	mix-blend-mode: screen;
	animation: orb-core var(--dur-core) ease-in-out infinite;
}

/*
 * A FOTO, dentro do vidro.
 *
 * `cover` porque o recorte é circular e uma imagem `contain` deixaria barras
 * dentro de uma esfera — o que se lê como um ecrã e não como um objeto. A
 * opacidade abaixo de 1 e a máscara radial são o que a mantém DENTRO: sem elas
 * a foto encosta ao aro e o orbe deixa de existir por trás dela.
 *
 * `pointer-events: none` porque isto não é um controlo — é a pele do objeto num
 * instante. O gesto de abrir a imagem continua a ser a miniatura da linha da
 * história, que é onde ele faz sentido.
 */
.ben-orb__photo {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: 50%;
	opacity: 0.88;
	pointer-events: none;
	/* Apaga o aro para a foto ficar suspensa no vidro em vez de o tapar. O prefixo
	   fica porque o Safari ainda o quer; sem máscara a foto só encosta à borda,
	   que é feio e nunca partido. */
	-webkit-mask-image: radial-gradient(circle at 50% 50%, #000 0%, #000 62%, transparent 96%);
	mask-image: radial-gradient(circle at 50% 50%, #000 0%, #000 62%, transparent 96%);
}

/*
 * A ENTRADA e a SAÍDA, sem sobressalto.
 *
 * 600 ms é o mesmo tempo em que o orbe muda de humor (`transition` do shell) —
 * uma foto que entrasse mais depressa do que a cor do objeto leria como um
 * elemento estranho a ser injectado. As duas fotos coexistem durante a troca
 * (`position: absolute` nas duas), portanto é uma dissolvência e nunca um corte.
 */
.orb-photo-enter-active,
.orb-photo-leave-active {
	transition:
		opacity 600ms ease,
		transform 600ms ease;
}

.orb-photo-enter-from,
.orb-photo-leave-to {
	opacity: 0;
	transform: scale(1.12);
}

/* The soul. Off-centre, because a centred point reads as a target. */
.ben-orb__spark {
	position: absolute;
	left: 50%;
	top: 50%;
	width: 22%;
	height: 22%;
	transform: translate(-54%, -50%);
	border-radius: 50%;
	background: radial-gradient(
		circle,
		#ffffff 0%,
		var(--spark-bright) 28%,
		var(--spark) 60%,
		rgba(255, 194, 74, 0) 90%
	);
	box-shadow:
		0 0 18px rgba(255, 194, 74, 0.9),
		0 0 36px rgba(255, 194, 74, 0.5);
	animation: orb-spark var(--dur-spark) ease-in-out infinite;
}

/* A band that crosses rarely. An always-on sweep is a loading shimmer. */
.ben-orb__sheen {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		115deg,
		transparent 38%,
		rgba(255, 255, 255, 0.28) 50%,
		transparent 62%
	);
	background-size: 260% 260%;
	animation: orb-sheen 9s ease-in-out infinite;
}

/*
 * ── O ORBE DO NEGÓCIO ─────────────────────────────────────────────────────
 *
 * As cores da marca entram pelas MESMAS variáveis do vidro, e não por outra
 * camada: o objeto é o mesmo, só a luz é dela. `@property` registra-as como
 * cores para a troca ser uma passagem de 1.2 s e não um corte — o valor inicial
 * é o do Ben, então quem não tem marca não muda nada.
 */
@property --orb-top { syntax: "<color>"; inherits: true; initial-value: #206de8; }
@property --orb-shell { syntax: "<color>"; inherits: true; initial-value: #0a3d8f; }

.ben-orb--business {
	transition:
		--orb-top 1.2s ease,
		--orb-shell 1.2s ease;
}

.ben-orb--brand {
	--orb-top: var(--brand-a);
	--orb-shell: color-mix(in srgb, var(--brand-a) 55%, #000);
}


/* A rampa do Ben girada para o laranja do copiloto: mesma profundidade, outra luz. */
.ben-orb--copilot {
	--orb-top: #f0761a;
	--orb-shell: #8f3a0a;
	--orb-deep: #281000;
	--orb-rim: #140700;
	--core-mid: #ffb070;
	--core-bright: #ffe6d0;
	--core-edge: 255, 140, 60;
}

.ben-orb--copilot.ben-orb--idle,
.ben-orb--copilot.ben-orb--thinking {
	--aura-rgb: 240, 118, 26;
}

/* A auréola do repouso e do pensar é da marca; sucesso e falha continuam a dizer o que são. */
.ben-orb--brand.ben-orb--idle,
.ben-orb--brand.ben-orb--thinking,
.ben-orb--brand.ben-orb--acting {
	--aura-rgb: var(--brand-rgb);
}

/* Uma descoberta chegou: um anel da cor da marca abre-se para fora, uma vez. */
.ben-orb__beat {
	position: absolute;
	inset: 0;
	border-radius: 50%;
	box-shadow: 0 0 0 2px rgba(var(--brand-rgb, var(--aura-rgb)), 0.7);
	animation: orb-beat 0.9s ease-out both;
	pointer-events: none;
}

@keyframes orb-beat {
	from {
		transform: scale(1);
		opacity: 1;
	}
	to {
		transform: scale(1.35);
		opacity: 0;
	}
}

/* ── Per state ───────────────────────────────────────────────────────────── */

.ben-orb--thinking {
	--speed: 2.4;
	--glow: 1.25;
}

.ben-orb--typing {
	--speed: 1.8;
	/* Green while the words come: the state everyone wants to recognise instantly. */
	--aura-rgb: 52, 211, 153;
}

.ben-orb--acting {
	--speed: 2;
	/*
	 * VIOLETA, e é o orbe que a herda de si mesmo.
	 *
	 * Este estado era azul-celeste — o que funcionava enquanto o orbe repousava
	 * em violeta. Com o repouso azul, os dois deixariam de se distinguir, e
	 * "estou a fazer alguma coisa" é justamente o estado que precisa saltar. O
	 * violeta que o orbe deixou de ser em repouso é a cor mais distante que
	 * ainda pertence à casa.
	 */
	--aura-rgb: 160, 32, 232;
	--glow: 1.3;
}

.ben-orb--success {
	--aura-rgb: 52, 211, 153;
	--glow: 1.4;
}

.ben-orb--trouble {
	--aura-rgb: 248, 113, 113;
	--glow: 1.3;
	animation: orb-shake 0.5s ease-in-out;
}

/*
 * The ring is for TOOLS specifically. A tool call is the agent reaching OUTSIDE itself, so
 * the mark for it is outside the orb — the only layer that is.
 */
.ben-orb__ring {
	position: absolute;
	inset: -14%;
	border-radius: 50%;
	border: 2px solid rgba(125, 211, 252, 0.75);
	border-top-color: transparent;
	border-right-color: transparent;
	animation: orb-spin 0.9s linear infinite;
}

.ben-orb__dots {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 11%;
}

.ben-orb__dots i {
	width: 11%;
	height: 11%;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.92);
	animation: orb-bounce 1.1s ease-in-out infinite;
}
.ben-orb__dots i:nth-child(2) {
	animation-delay: 0.15s;
}
.ben-orb__dots i:nth-child(3) {
	animation-delay: 0.3s;
}

.ben-orb__mark {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: calc(var(--orb-size) * 0.42);
	font-weight: 700;
	color: #ffffff;
	text-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);
	animation: orb-pop 0.35s ease-out;
}

/* ── Keyframes ───────────────────────────────────────────────────────────── */

@keyframes orb-breath {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.045);
	}
}

@keyframes orb-aura {
	0%,
	100% {
		opacity: 0.85;
		transform: scale(1);
	}
	50% {
		opacity: 1;
		transform: scale(1.08);
	}
}

/*
 * The core's path. NOT a circle: a circular path reads as the sphere rotating, which is a
 * different and busier idea than light moving inside it.
 */
@keyframes orb-core {
	0%,
	100% {
		transform: translate(-50%, -50%) scale(1);
	}
	25% {
		transform: translate(-53%, -48%) scale(1.04);
	}
	50% {
		transform: translate(-47%, -52%) scale(0.97);
	}
	75% {
		transform: translate(-51%, -53%) scale(1.02);
	}
}

@keyframes orb-spark {
	0%,
	100% {
		opacity: 0.85;
		transform: translate(-54%, -50%) scale(1);
	}
	45% {
		opacity: 1;
		transform: translate(-54%, -50%) scale(1.12);
	}
}

@keyframes orb-sheen {
	0%,
	70% {
		background-position: 140% 0;
		opacity: 0;
	}
	80% {
		opacity: 0.5;
	}
	100% {
		background-position: -40% 0;
		opacity: 0;
	}
}

@keyframes orb-spin {
	to {
		transform: rotate(360deg);
	}
}

@keyframes orb-bounce {
	0%,
	100% {
		transform: translateY(18%);
		opacity: 0.55;
	}
	50% {
		transform: translateY(-18%);
		opacity: 1;
	}
}

@keyframes orb-pop {
	from {
		transform: scale(0.6);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes orb-shake {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-8%);
	}
	75% {
		transform: translateX(8%);
	}
}

/*
 * Reduced motion: everything stops, and the object still says what it is.
 *
 * The sheen goes to zero rather than freezing — a stopped gradient band is a bright stripe
 * across the orb that reads as a rendering fault, worse than the animation it replaces.
 * The core and the spark STAY, because they are not motion: they are where the light is,
 * and removing them is what leaves a flat disc.
 */
@media (prefers-reduced-motion: reduce) {
	.ben-orb__shell,
	.ben-orb__aura,
	.ben-orb__core,
	.ben-orb__spark,
	.ben-orb__sheen,
	.ben-orb__ring,
	.ben-orb__dots i,
	.ben-orb__mark,
	.ben-orb--trouble {
		animation: none;
	}

	.ben-orb__beat {
		display: none;
	}

	.ben-orb__sheen {
		opacity: 0;
	}

	/*
	 * A foto TROCA, não se move.
	 *
	 * Quem pediu menos movimento continua a ver o que a missão encontrou — a
	 * informação não é do movimento, é da imagem. O que sai é a dissolvência e o
	 * zoom: a foto nova aparece no lugar da anterior, sem transição nenhuma.
	 */
	.orb-photo-enter-active,
	.orb-photo-leave-active {
		transition: none;
	}

	.orb-photo-enter-from,
	.orb-photo-leave-to {
		opacity: 1;
		transform: none;
	}

	/* Busy, without moving. */
	.ben-orb--thinking .ben-orb__aura,
	.ben-orb--acting .ben-orb__aura {
		opacity: 1;
	}
}
</style>
